import { useEffect, useRef, useState } from "react";
import type {
  CopilotBlockingReading,
  CopilotChip,
  CopilotFinding,
  CopilotKept,
} from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import type { PspRules } from "@CommonTypes/backend/PspRules";
import { TELL_O_COPY } from "@CommonUtils/pog/tellO";
import { Color, Flex, Scroller, Spinner, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import {
  isRedoPatch,
  LinkButton,
  OButton,
  OChange,
  OChanges,
  OFixes,
  OLastRun,
  OMark,
  ORaised,
  OSays,
  OSetup,
  type OSetRow,
  redoTitleOf,
  SuggestionChip,
  undoneBy,
  UserSays,
  Wording,
} from "./components/OParts";
import { OSeen, sayOnce } from "./components/OOpening";
import { OConcierge } from "./components/OConcierge";
import { OSketch } from "./components/OSketch";
import {
  cardKey,
  changeKey,
  isDoing,
  isOpenCdtStudio,
  isGenerateAgain,
  isRunAsIs,
  overlaps,
  useConfigCopilot,
  type CopilotEntry,
  type CopilotPendingFix,
  type OpenedStore,
} from "./store/useConfigCopilot";

export const Composer = styledSafe("form")`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 14px;
  border: 1px solid ${Color.spaceGray};
  border-radius: 20px;
  background: ${Color.white};

  &:focus-within {
    border-color: ${Color.primary};
  }
`;

export const ComposerInput = styledSafe("input")`
  flex-grow: 1;
  min-width: 0;
  border: none;
  outline: none;
  font: inherit;
  font-size: 13px;
  background: transparent;
`;

export const SendButton = styledSafe("button")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: ${Color.primary};
  color: ${Color.white};
  font-size: 15px;
  cursor: pointer;

  &:disabled {
    background: ${Color.primaryDisabled};
    cursor: default;
  }
`;

interface Props {
  merchflowId: number;
  basePogId: number;
  blocking: CopilotBlockingReading | null;

  readRules: () => PspRules;
  writeRules: (rules: PspRules) => void;
  onAdvanced: () => void;

  isShown: boolean;

  // In a concierge flow, Generate also waits for the required decisions.
  onSettled?: (isSettled: boolean) => void;
  onConciergeReady?: (ready: boolean | null) => void;

  onMarks?: (marks: Readonly<Record<string, string>>) => void;

  pendingFix?: CopilotPendingFix;

  onGenerate?: () => void;

  onOpenStore?: (opened: OpenedStore) => void;

  onOpenRun?: (storePogTriggerId: number) => void;

  onOpenCdtStudio?: () => void;
}

const textOf = (entry: CopilotEntry) =>
  entry.blocks
    .flatMap((block) => (block.type === "text" && block.text ? [block.text] : []))
    .join(" ");

export const ConfigCopilotPanel = ({
  merchflowId,
  basePogId,
  blocking,
  readRules,
  writeRules,
  onAdvanced,
  isShown,
  onSettled,
  onConciergeReady,
  onMarks,
  pendingFix,
  onGenerate,
  onOpenStore,
  onOpenRun,
  onOpenCdtStudio,
}: Props) => {
  const { t } = useTranslation("common");
  const o = useConfigCopilot({
    merchflowId,
    basePogId,
    blocking,
    readRules,
    writeRules,
    pendingFix,
    onGenerate,
  });
  const [draft, setDraft] = useState("");
  const [selectDraft, setSelectDraft] = useState(false);

  const [isAdjustOpen, setIsAdjustOpen] = useState<boolean | null>(null);
  const [hasChanged, setHasChanged] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const thread = useRef<HTMLDivElement>(null);
  const terminalSummary = useRef<HTMLDivElement>(null);

  useEffect(
    () => onSettled?.(o.briefFailed || (o.isReady && (!o.concierge || o.conciergeReady === true))),
    [o.isReady, o.briefFailed, o.concierge, o.conciergeReady],
  );
  useEffect(
    () => onConciergeReady?.(o.concierge ? o.conciergeReady === true : null),
    [o.concierge, o.conciergeReady],
  );
  useEffect(() => onMarks?.(o.marks), [o.marks]);

  // The opening's own rows own their changes, matched by path; the strip shows only the rest.
  const setupPaths = [
    ...o.setup.flatMap(({ change }) => change?.paths ?? []),
    ...o.setupApplied.flatMap(({ paths }) => paths),
  ];
  const strip = o.changes.filter(
    (change) => !change.paths.some((path) => setupPaths.some((one) => overlaps(path, one))),
  );
  const isOpening = o.observations.length > 0 || o.setup.length > 0 || o.kept.length > 0;

  // Update during render so opening the rules and scrolling the thread share one commit.

  if (!hasChanged && (strip.length > 0 || o.entries.some((entry) => entry.applied))) {
    setHasChanged(true);
  }

  const hasBlocker = o.findings.some((finding) => finding.severity === "blocker");

  const hasFixes = hasBlocker || o.findings.some((finding) => finding.fix);
  // The opening's What I set already says each change, so a change alone keeps Adjust shut there.
  const showsAdjust = isAdjustOpen ?? ((hasChanged && !isOpening) || hasBlocker || o.isAdjustAsked);

  const wasShown = useRef(isShown);
  useEffect(() => {
    if (!isShown && wasShown.current) o.leaveForAdvanced();
    if (isShown && !wasShown.current) o.backFromAdvanced();
    wasShown.current = isShown;
  }, [isShown]);

  useEffect(() => {
    if (o.concierge) {
      if (o.conciergeReady) {
        terminalSummary.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
        return;
      }
      thread.current?.parentElement
        ?.querySelector<HTMLElement>('[data-testid="o-concierge-decision"]')
        ?.scrollIntoView?.({ block: "start", behavior: "smooth" });
      return;
    }
    const latest = o.entries.at(-1);
    if (!latest) return;
    if (
      latest.role === "copilot" &&
      latest.blocks.some((block) => block.type === "chips" && block.chips.some(isRunAsIs))
    ) {
      terminalSummary.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
      return;
    }
    if (
      latest.role === "copilot" &&
      latest.blocks.some(
        (block) => block.type === "observation" && block.observation.id.startsWith("ask_"),
      )
    ) {
      const replies = thread.current?.querySelectorAll<HTMLElement>(
        '[data-testid="copilot-reply"]',
      );
      replies?.item(replies.length - 1)?.scrollIntoView?.({ block: "start", behavior: "smooth" });
      return;
    }
    end.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
  }, [o.entries.length, o.concierge, o.conciergeReady]);

  useEffect(() => {
    if (!selectDraft) return;
    input.current?.focus();
    input.current?.select();
    setSelectDraft(false);
  }, [selectDraft]);

  const send = async () => {
    const text = draft.trim();
    if (!text || o.isAsking) return;
    setDraft("");

    input.current?.focus();
    const isDone = await o.ask({ text });

    if (!isDone) setDraft((now) => now || text);
  };

  const tap = (chip: CopilotChip) => {
    const pick = chip.pick;
    if (pick?.kind === "fix" && onGenerate) {
      const finding = o.findings.find((one) => one.id === pick.finding);
      if (finding?.fix && isGenerateAgain(finding.fix)) {
        onGenerate();
        return;
      }
    }
    if (onGenerate && isRunAsIs(chip)) {
      onGenerate();
      return;
    }
    if (onOpenCdtStudio && isOpenCdtStudio(chip)) {
      onOpenCdtStudio();
      return;
    }
    const opened = chip.pick && onOpenStore ? o.storeOpenedBy(chip.pick) : null;
    if (opened) {
      onOpenStore?.(opened);
      return;
    }
    if (chip.pick) {
      void o.ask({ pick: chip.pick, shown: chip.label });
    } else if (chip.say) {
      setDraft(chip.say);
      setSelectDraft(true);
    }
  };

  const fix = (finding: CopilotFinding) => {
    // A failed run's "Generate again" changes no rule: the page's own Generate runs it.
    if (onGenerate && finding.fix && isGenerateAgain(finding.fix)) {
      onGenerate();
      return;
    }
    const opened =
      onOpenStore && finding.fix
        ? o.storeOpenedBy({ kind: "fix", finding: finding.id, patch: finding.fix })
        : null;
    if (opened) onOpenStore?.(opened);
    else o.fix(finding);
  };

  const chipsOf = (chips: readonly CopilotChip[], showTradeoffs = false) => {
    const doing = chips.filter(isDoing);
    if (doing.length === 0) return null;
    return (
      <Flex gap="6px" flexWrap="wrap">
        {doing.map((chip) => {
          const tradeoff = chip.tradeoff;
          if (!showTradeoffs || !tradeoff?.gain || !tradeoff.cost)
            return (
              <SuggestionChip
                key={chip.label}
                type="button"
                disabled={o.isAsking}
                onClick={() => tap(chip)}
              >
                {chip.label}
              </SuggestionChip>
            );
          return (
            <Flex
              key={chip.label}
              column
              gap="4px"
              padding="8px"
              border={`1px solid ${Color.lightGrayHover}`}
              borderRadius="4px"
              data-testid="o-tradeoff"
            >
              {tradeoff.recommended && (
                <Text variant="small3" bold>
                  {t("configCopilot.recommendedChoice")}
                </Text>
              )}
              {tradeoff.selected && (
                <Text variant="small3" bold>
                  {t("configCopilot.currentChoice")}
                </Text>
              )}
              <SuggestionChip type="button" disabled={o.isAsking} onClick={() => tap(chip)}>
                {chip.label}
              </SuggestionChip>
              <Text variant="small1">{tradeoff.gain}</Text>
              <Text variant="small1" color={Color.textSecondary}>
                {t("configCopilot.choiceCost", { cost: tradeoff.cost })}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  const reply = (entry: CopilotEntry) => {
    const text = textOf(entry);
    const appliedKey = cardKey(entry.id, "applied");
    const { applied } = entry;
    const appliedState = applied && o.cardState(appliedKey, applied.patch);

    const isOffStrip =
      appliedState === "on" &&
      !applied?.changes?.some((row) =>
        o.changes.some((shown) => changeKey(shown) === changeKey(row)),
      );

    const hasRedoCard = o.entries.some((later) => undoneBy(o.entries, later)?.id === entry.id);
    return (
      <Flex key={entry.id} column gap="8px" data-testid="copilot-reply">
        {text && <OSays size={22}>{text}</OSays>}

        <Flex column gap="6px" padding="0 0 0 32px">
          {applied && (
            <>
              {/* An undo O made keeps its card: its one button is Redo. */}
              {isRedoPatch(applied.patch) ? (
                <OChange
                  patch={{
                    ...applied.patch,
                    title: redoTitleOf(o.entries, { ...entry, applied }, t("configCopilot.redo")),
                  }}
                  state={appliedState ?? "offered"}
                  isMadeByO
                  onApply={() => o.use(appliedKey, applied.patch)}
                  onUndo={() => o.undo(appliedKey, applied.patch)}
                />
              ) : isOffStrip ? (
                <Flex>
                  <LinkButton type="button" onClick={() => o.undo(appliedKey, applied.patch)}>
                    {TELL_O_COPY.undo}
                  </LinkButton>
                </Flex>
              ) : (
                appliedState === "again" &&
                !hasRedoCard && (
                  <Flex>
                    <LinkButton type="button" onClick={() => o.use(appliedKey, applied.patch)}>
                      {t("configCopilot.useAgain")}
                    </LinkButton>
                  </Flex>
                )
              )}
              {appliedState === "on" && <ORaised findings={applied.raises} onFix={fix} />}
            </>
          )}

          {entry.blocks.map((block, index) => {
            const key = cardKey(entry.id, index);
            if (block.type === "patch") {
              const state = o.cardState(key, block.patch);
              return (
                <Flex key={key} column gap="4px">
                  <OChange
                    patch={block.patch}
                    state={state}
                    onApply={() => o.use(key, block.patch)}
                    onUndo={() => o.undo(key, block.patch)}
                  />
                  {state === "on" && <ORaised findings={block.raises ?? []} onFix={fix} />}
                </Flex>
              );
            }
            if (block.type === "findings") {
              return <OFixes key={key} findings={block.findings} onFix={fix} />;
            }
            if (block.type === "observation") {
              return (
                <Flex key={key} column gap="8px" data-testid="o-current-evidence">
                  <Text variant="body2">{block.observation.line}</Text>
                  {block.observation.sketch && (
                    <OSketch pair={block.observation.sketch} palette={o.palette} />
                  )}
                </Flex>
              );
            }
            if (block.type === "chips") {
              const shown = chipsOf(block.chips, true);
              return shown && <Flex key={key}>{shown}</Flex>;
            }
            return null;
          })}
        </Flex>
      </Flex>
    );
  };

  const stuckRun = o.runState === "stuck" ? o.lastRun : null;
  const isStuck = stuckRun !== null;
  const once = sayOnce();
  once(o.headline);
  const running =
    o.lastRun && o.lastRun.running > 0 && !isStuck
      ? t("configCopilot.lastRunRunning", { done: o.lastRun.done, stores: o.lastRun.stores })
      : null;
  once(running);
  if (hasFixes) o.findings.forEach(({ title }) => once(title));
  const seen = o.observations.filter(({ line }) => once(line));
  // The brief keeps future questions for O's grounding. Show only the question
  // being asked now; subsequent turns carry their own question in the thread.
  const statements = seen.filter(({ id }) => !id.startsWith("ask_"));
  const openingQuestionVisible =
    o.entries.every(({ id }) => id === o.openingEntry) ||
    (o.isAsking &&
      o.entries.some(({ role }) => role === "user") &&
      !o.entries.some(({ role }) => role === "copilot"));
  const currentAsk = openingQuestionVisible
    ? seen.filter(({ id }) => id.startsWith("ask_")).slice(0, 1)
    : [];
  const keyEvidence = [...currentAsk, ...statements.slice(0, currentAsk.length ? 0 : 2)];
  const moreSettings = statements.slice(currentAsk.length ? 0 : 2);

  const keptLine = ({ yours, oWould, reason }: CopilotKept) =>
    reason.trim()
      ? t("configCopilot.kept", { yours, oWould, reason: reason.trim() })
      : t("configCopilot.keptNoReason", { yours, oWould });
  const lineOnce = (line: string) => (once(line) ? line : undefined);
  const setRows: OSetRow[] = [
    ...o.setup.map((row, index): OSetRow => {
      const kept = o.kept.find(({ label }) => label === row.label);
      const state = o.setupStateOf(row);
      return {
        key: `set-${index}`,
        label: row.label,
        paths: row.change?.paths ?? [],
        value: row.value,
        line: lineOnce(kept ? keptLine(kept) : row.reason),
        isKept: kept !== undefined,
        isWording: o.isWording && !kept,
        action:
          (state === "on" && { label: TELL_O_COPY.undo, onClick: () => o.undoSetup(row) }) ||
          (state === "off" && {
            label: t("configCopilot.useAgain"),
            onClick: () => o.setupAgain(row),
          }) ||
          undefined,
      };
    }),
    ...o.kept
      .filter((kept) => !o.setup.some(({ label }) => label === kept.label))
      .map(
        (kept, index): OSetRow => ({
          key: `kept-${index}`,
          label: kept.label,
          paths: [],
          line: lineOnce(keptLine(kept)),
          isKept: true,
        }),
      ),
  ];
  // An always-on phrase reads "{label} {value}": one a What I set row already says is left out.
  const setLabels = setRows.map(({ label }) => label.trim().toLowerCase());
  const alwaysOn = o.alwaysOn.filter((phrase) => {
    const said = phrase.trim().toLowerCase();
    return !setLabels.some((label) => said === label || said.startsWith(`${label} `));
  });

  const stuckChips = stuckRun && (
    <Flex gap="6px" flexWrap="wrap" data-testid="o-stuck">
      {onGenerate && (
        <SuggestionChip type="button" onClick={onGenerate}>
          {t("configCopilot.runAgain")}
        </SuggestionChip>
      )}
      {onOpenRun && (
        <SuggestionChip type="button" onClick={() => onOpenRun(stuckRun.storePogTriggerId)}>
          {t("configCopilot.openStorePlanograms")}
        </SuggestionChip>
      )}
    </Flex>
  );

  if (isStuck && onGenerate) once(t("configCopilot.runAgain"));
  if (isStuck && onOpenRun) once(t("configCopilot.openStorePlanograms"));
  const asks = o.asks.filter(({ label }) => once(label));
  // A change the model made with its opening is still the opening, not a conversation.
  const isAsking = isOpening && openingQuestionVisible && asks.length > 0;
  const question = isAsking && once(o.question) ? o.question : null;
  const latestReply = o.entries.findLast(({ role }) => role === "copilot");
  const turnQuestionActive =
    (latestReply?.blocks.some(
      (block) => block.type === "observation" && block.observation.id.startsWith("ask_"),
    ) ??
      false) &&
    (latestReply?.blocks.some((block) => block.type === "chips" && block.chips.some(isDoing)) ??
      false);
  const hasActiveQuestion = isAsking || turnQuestionActive;
  const terminalReply =
    !hasActiveQuestion &&
    latestReply?.blocks.some((block) => block.type === "chips" && block.chips.some(isRunAsIs))
      ? latestReply
      : null;
  const conciergeSummary = o.conciergeReady ? latestReply : null;
  const recommendation = hasActiveQuestion || terminalReply ? null : o.recommendation;
  once(recommendation?.label);
  const footerChips = hasActiveQuestion || terminalReply ? [] : o.suggestions;

  return (
    <Flex column flexGrow minHeight="0" data-testid="config-copilot">
      <Flex
        align="center"
        justify="between"
        gap="12px"
        padding="14px 20px"
        borderBottom={`1px solid ${Color.lightGrayHover}`}
      >
        <Flex align="center" gap="12px">
          <OMark size={34} />
          <Flex column>
            <Text variant="body1" bold>
              {t("configCopilot.name")}
            </Text>
            <Text variant="small1" color={Color.textSecondary}>
              {t("configCopilot.tagline")}
            </Text>
          </Flex>
        </Flex>

        <LinkButton type="button" onClick={onAdvanced} data-testid="o-advanced">
          {t("configCopilot.advanced")} ›
        </LinkButton>
      </Flex>

      <Scroller gutter="stable">
        <Flex column gap="20px" padding="18px 20px">
          {!o.isReady && !o.briefFailed && (
            <Flex gap="10px" align="center">
              <OMark size={26} />
              <Spinner />
              <Text variant="body2" color={Color.textSecondary}>
                {t("configCopilot.thinking")}
              </Text>
            </Flex>
          )}

          {o.briefFailed && (
            <OSays size={22}>
              <Flex gap="10px" align="center" data-testid="o-brief-failed">
                <Text variant="body2" color={Color.alertHighText}>
                  {t("configCopilot.briefFailed")}
                </Text>
                <LinkButton type="button" onClick={o.retry}>
                  {t("configCopilot.retry")}
                </LinkButton>
              </Flex>
            </OSays>
          )}

          {o.isReady && o.concierge && (
            <>
              {conciergeSummary && (
                <div ref={terminalSummary} data-testid="o-terminal-summary">
                  {reply(conciergeSummary)}
                </div>
              )}
              <OConcierge
                sections={o.concierge}
                ready={o.conciergeReady === true}
                palette={o.palette}
                disabled={o.isAsking}
                onPick={tap}
                onRetry={o.retry}
              />
              {hasBlocker && (
                <Text variant="small1" color={Color.alertHighText}>
                  {t("configCopilot.blockerInDetails")}
                </Text>
              )}
              {(o.findings.length > 0 || o.lastRun || strip.length > 0) && (
                <details data-testid="o-concierge-diagnostics">
                  <summary>{t("configCopilot.details")}</summary>
                  <Flex column gap="12px" padding="12px 0 0 0">
                    {o.findings.length > 0 && <OFixes findings={o.findings} onFix={fix} />}
                    {strip.length > 0 && (
                      <OChanges
                        changes={strip}
                        sinceRun={o.hasLastRunRules}
                        onUndo={o.undoChange}
                        onBack={o.backToStart}
                      />
                    )}
                    {o.lastRun && (
                      <OButton
                        type="button"
                        aria-busy={o.isDetailsLoading}
                        onClick={() => !o.isDetailsLoading && o.toggleRunDetails()}
                      >
                        {t("configCopilot.lastRunStores")}
                      </OButton>
                    )}
                    {o.runDetails && <OLastRun details={o.runDetails} onFix={fix} />}
                  </Flex>
                </details>
              )}
            </>
          )}

          {o.isReady && !o.concierge && (
            <>
              {isAsking && (
                <Flex column gap="10px" data-testid="o-active-opening">
                  <OSeen
                    observations={keyEvidence}
                    isFolded={false}
                    palette={o.palette}
                    isWording={o.isWording}
                  />
                  <Flex column gap="10px" data-testid="o-active-question">
                    {question ? (
                      <Text variant="body2">
                        <span data-testid="o-question">{question}</span>
                      </Text>
                    ) : (
                      <Text variant="small3" color={Color.textSecondary} textTransform="uppercase">
                        {t("configCopilot.anythingToChange")}
                      </Text>
                    )}
                    {chipsOf(asks, true)}
                  </Flex>
                  {hasBlocker && (
                    <Text variant="small1" color={Color.alertHighText}>
                      {t("configCopilot.blockerInDetails")}
                    </Text>
                  )}
                </Flex>
              )}

              {terminalReply && (
                <div ref={terminalSummary} data-testid="o-terminal-summary">
                  {reply(terminalReply)}
                </div>
              )}

              <Flex column gap="10px" data-testid="o-verdict-first">
                <Text variant="body1">
                  <Wording data-testid="o-headline" aria-busy={o.isWording}>
                    {o.headline}
                  </Wording>
                </Text>
                {running && !hasActiveQuestion && (
                  <Text variant="small1" color={Color.textSecondary}>
                    <span data-testid="o-running">{running}</span>
                  </Text>
                )}
                {!hasActiveQuestion && stuckChips}
                <Flex gap="8px" align="center" flexWrap="wrap" data-testid="o-verdict-actions">
                  {recommendation && (
                    <OButton
                      type="button"
                      isPrimary
                      data-testid="o-recommendation"
                      onClick={() => {
                        o.tapRecommendation(recommendation);
                        tap(recommendation);
                      }}
                    >
                      {recommendation.label}
                    </OButton>
                  )}
                  <OButton
                    type="button"
                    aria-expanded={showsAdjust}
                    aria-controls="o-adjust"
                    data-testid="o-adjust-toggle"
                    onClick={() => setIsAdjustOpen(!showsAdjust)}
                  >
                    {t("configCopilot.adjust")}
                  </OButton>
                  {o.lastRun && (
                    <OButton
                      type="button"
                      aria-expanded={o.runDetails !== null}
                      aria-busy={o.isDetailsLoading}
                      data-testid="o-details"
                      onClick={() => !o.isDetailsLoading && o.toggleRunDetails()}
                    >
                      {t("configCopilot.details")}
                    </OButton>
                  )}
                </Flex>
              </Flex>

              {hasFixes && ((!hasActiveQuestion && !terminalReply) || o.runDetails) && (
                <OFixes findings={o.findings} onFix={fix} />
              )}

              {o.runDetails && <OLastRun details={o.runDetails} onFix={fix} />}

              {isOpening && !isAsking && !terminalReply && (
                <>
                  <OSeen
                    observations={keyEvidence}
                    isFolded={false}
                    palette={o.palette}
                    isWording={o.isWording}
                  />
                  <OSeen
                    observations={moreSettings}
                    isFolded
                    foldLabel={t("configCopilot.moreSettings")}
                    palette={o.palette}
                    isWording={o.isWording}
                  />
                  {setRows.length > 0 && !showsAdjust && (
                    <OSetup
                      title={t("configCopilot.whatISet")}
                      rows={setRows}
                      choices={[]}
                      alwaysOn={[]}
                      onPick={o.pickOption}
                    />
                  )}
                </>
              )}

              {isAsking && isOpening && (
                <>
                  <OSeen
                    observations={moreSettings}
                    isFolded
                    foldLabel={t("configCopilot.moreSettings")}
                    palette={o.palette}
                    isWording={o.isWording}
                  />
                  {setRows.length > 0 && !showsAdjust && (
                    <details data-testid="o-opening-settings">
                      <summary>{t("configCopilot.whatISet")}</summary>
                      <OSetup rows={setRows} choices={[]} alwaysOn={[]} onPick={o.pickOption} />
                    </details>
                  )}
                </>
              )}

              <OChanges
                changes={strip}
                sinceRun={o.hasLastRunRules}
                onUndo={o.undoChange}
                onBack={o.backToStart}
              />

              {showsAdjust && (
                <Flex column gap="20px" id="o-adjust" data-testid="o-adjust">
                  {!hasFixes && <OFixes findings={o.findings} onFix={fix} />}

                  <OSetup
                    title={isOpening ? t("configCopilot.whatISet") : undefined}
                    rows={isOpening ? setRows : undefined}
                    choices={o.choices}
                    idle={o.idle}
                    alwaysOn={alwaysOn}
                    onPick={o.pickOption}
                  />
                </Flex>
              )}
            </>
          )}

          <Flex
            ref={thread}
            column
            gap="12px"
            aria-live="polite"
            data-testid="o-thread"
            padding={o.entries.length > 0 && !o.concierge ? "16px 0 0 0" : undefined}
            borderTop={
              o.entries.length > 0 && !o.concierge ? `1px solid ${Color.lightGrayHover}` : undefined
            }
          >
            {!o.concierge &&
              o.entries
                .filter((entry) => entry.id !== terminalReply?.id)
                .map((entry) =>
                  entry.role === "user" ? (
                    <UserSays key={entry.id}>{textOf(entry)}</UserSays>
                  ) : (
                    reply(entry)
                  ),
                )}

            {o.concierge && o.entries.length > 0 && (
              <details data-testid="o-concierge-history">
                <summary>{t("configCopilot.conciergeHistory")}</summary>
                <Flex column gap="12px" padding="10px 0 0 0">
                  {o.entries
                    .filter((entry) => entry.id !== conciergeSummary?.id)
                    .map((entry) =>
                      entry.role === "user" ? (
                        <UserSays key={entry.id}>{textOf(entry)}</UserSays>
                      ) : (
                        reply(entry)
                      ),
                    )}
                </Flex>
              </details>
            )}

            {o.isThinking && (
              <Flex gap="10px" align="center" data-testid="o-thinking">
                <OMark size={22} />
                <Text variant="body2" color={Color.textSecondary}>
                  {TELL_O_COPY.thinking}
                </Text>
              </Flex>
            )}

            {o.trouble && (
              <OSays size={22}>
                <Flex gap="10px" align="center" data-testid="o-trouble">
                  <Text variant="body2" color={Color.alertHighText}>
                    {o.trouble.line}
                  </Text>
                  {o.trouble.retry && (
                    <LinkButton type="button" onClick={o.trouble.retry}>
                      {t("configCopilot.retry")}
                    </LinkButton>
                  )}
                </Flex>
              </OSays>
            )}
          </Flex>

          <div ref={end} />
        </Flex>
      </Scroller>

      <Flex
        column
        gap="8px"
        padding="12px 20px"
        borderTop={`1px solid ${Color.lightGrayHover}`}
        data-testid="o-composer-footer"
      >
        {/* O's recommendation sits beside the verdict: not a second time here. */}
        {chipsOf(footerChips)}

        <Composer
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <ComposerInput
            ref={input}
            value={draft}
            aria-label={t("configCopilot.askO")}
            placeholder={t("configCopilot.placeholder")}
            data-testid="copilot-input"
            disabled={!o.isReady}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <SendButton
            type="submit"
            aria-label={t("configCopilot.send")}
            disabled={o.isAsking || !draft.trim()}
          >
            ↑
          </SendButton>
        </Composer>
      </Flex>
    </Flex>
  );
};
